import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, MapPin, DollarSign, Loader2, Star, BadgeCheck } from "lucide-react";

export default function FavoritesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/favorites"); setItems(data.results); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (sitterId) => {
    try {
      await api.post("/favorites", { sitter_id: sitterId });
      toast.success("Removed from favorites"); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="favorites-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <span className="section-eyebrow">Saved sitters</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
          <Heart className="w-6 h-6 text-danger fill-danger/40" /> Favorites
        </h1>
        <p className="text-stone2-600 mt-1 text-sm">
          Free plan allows 2 favorites. <Link to="/pricing" className="font-semibold text-sage-800 hover:underline">Upgrade for unlimited</Link>.
        </p>

        {loading && <div className="mt-8 text-stone2-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="mt-8 paper-card p-8 text-center text-stone2-500">
            No favorites yet. <Link to="/search" className="font-semibold text-sage-800 hover:underline">Find sitters</Link>.
          </div>
        )}
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {items.map((f) => {
            const s = f.sitter;
            if (!s) return null;
            return (
              <article key={f.id} className="paper-card p-5" data-testid={`favorite-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold">
                      {s.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 font-heading font-semibold text-sage-900">
                        {s.name}
                        {s.verified && <BadgeCheck className="w-4 h-4 text-sage-600" />}
                      </div>
                      <div className="text-xs text-stone2-500 flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> {s.city || "—"}
                        <DollarSign className="w-3 h-3" /> {s.hourly_rate}/hr
                        <Star className="w-3 h-3 text-ochre-500 fill-ochre-500" /> {s.rating_avg || "—"}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => remove(s.id)}
                    data-testid={`favorite-remove-${s.id}`}
                    title="Remove from favorites"
                    className="p-2 rounded-full text-danger hover:bg-danger/5">
                    <Heart className="w-4 h-4 fill-danger" />
                  </button>
                </div>
                <div className="mt-3">
                  <Link to={`/sitters/${s.id}`} className="btn-primary text-xs">View profile</Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
